"use client";
import { WeatherPageProps } from "@/app/(pages)/Home/types";
import { getCityClockFromWeather } from "@/utils/cityTime";
import { TFunction } from "i18next";

export function buildWeatherPageModel({
  city,
  weather,
  lat,
  lon,
  t,
  language,
}: Omit<WeatherPageProps, "isFirstEnter" | "country"> & {
  t: TFunction<"translation", undefined>;
  language: string;
}) {
  const { hour: currentHour, todayKey: currentDay } =
    getCityClockFromWeather(weather);

  const days = Object.values(weather)
    .map((d) => d.date)
    .sort((a, b) => (a < b ? -1 : 1));

  const startISO = days[0].slice(0, 10);
  const endISO = days[days.length - 1].slice(0, 10);

  const safe = weather[currentDay] ?? weather[days[0]];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Dataset",
    name: t(`jsonld.name`, { city }),
    description: safe
      ? t("jsonld.description.full", {
          city,
          daysCount: days.length - 1,
          minTemp: safe.minTemperature,
          maxTemp: safe.maxTemperature,
          feelsLike: safe.feelsLike[currentHour],
          windSpeed: safe.windSpeed[currentHour],
        })
      : t("jsonld.description.fallback", { city }),
    temporalCoverage: `${startISO}/${endISO}`,
    spatialCoverage: {
      "@type": "Place",
      name: city,
      geo: { "@type": "GeoCoordinates", latitude: lat, longitude: lon },
    },
    keywords: t("jsonld.keywords", { returnObjects: true, city }) as string[],
    variableMeasured: t("jsonld.variableMeasured", {
      returnObjects: true,
    }) as string[],
    publisher: {
      "@type": "Organization",
      name: "Verso",
      url: "https://verso.app",
    },
    inLanguage: language,
  };

  return { jsonLd, currentDay, currentHour };
}
